<script lang="ts">
  import { browser } from "$app/environment";

  import { createDropdownMenu, melt } from "@melt-ui/svelte";
  import type { Snippet } from "svelte";

  export let align: "left" | "right" | "full" = "left";
  export let isPortalled: boolean = false;
  export let closeOnInnerClick: boolean = true;
  export const button: Snippet | undefined = undefined;

  const alignMap: Record<
    "left" | "right" | "full",
    "bottom-start" | "bottom-end" | "bottom"
  > = {
    left: "bottom-start",
    right: "bottom-end",
    full: "bottom",
  };

  let placement = alignMap[align];
  let menuElement: HTMLDivElement | null = null;

  const {
    elements: { menu, trigger },
    states: { open },
    builders: { createSubmenu },
  } = createDropdownMenu({
    positioning: {
      placement,
      flip: false,
      sameWidth: align === "full",
    },
    preventScroll: false,
    // Determines which element to render the dropdown menu into.
    // undefined uses the parent portal; null disables the portalling
    portal: isPortalled ? undefined : null,
  });

  // HACK: This is a workaround because melt-ui doesn't properly close
  // menus when other menu triggers are selected.
  // <https://github.com/melt-ui/melt-ui/issues/1196>
  const handleClick = (e: MouseEvent) => {
    if ($open) {
      const target = e.target as HTMLElement;

      if (target.closest("button, a") === null) {
        return;
      }

      if (menuElement && menuElement.contains(target)) {
        // Inner click
        if (closeOnInnerClick) {
          closeMenu();
        }
      } else {
        // Outer clicks should close menu if not button or a
        closeMenu();
      }
    }
  };

  const closeMenu = () => {
    open.set(false);
  };

  // Add event listener only while the menu is open. Needs to be done in
  // `setTimeout()`, otherwise the trigger closes the menu immediately.
  $: if (browser) {
    if ($open) {
      setTimeout(() => {
        document.body.addEventListener("click", handleClick);
      });
    } else {
      document.body.removeEventListener("click", handleClick);
    }
  }
</script>

<span class="flex" use:melt={$trigger}>
  <slot name="button" />
</span>

{#if $open}
  <div use:melt={$menu} class="outline-none" bind:this={menuElement}>
    <div class="relative z-20">
      <div class="menu-items" class:w-full={align === "full"}>
        <slot {createSubmenu} {closeMenu} />
      </div>
    </div>
  </div>
{/if}

<style lang="postcss">
  .menu-items {
    @apply rounded-lg border border-white/10 bg-ground/50 backdrop-blur-md inline-block;
  }

  .menu-items :global(hr) {
    @apply border-white/10 my-1.5;
  }
</style>
